// Shared landing primitives: brand logo (retained equalizer mark), reveal
// hook, buttons, section scaffolding. Kept dependency-light — only React,
// react-router Link, and lucide icons already in the bundle.
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

// ─── Logo — the equalizer-bar mark retained from the prior brand, + wordmark ──
export function LogoMark({ size = 30 }: { size?: number }) {
  // Six accent bars at three opacity tiers — "Orkestra = many parts, one score".
  const bars = [
    { x: 6,  h: 22, o: 0.55 }, { x: 15, h: 34, o: 0.8 }, { x: 24, h: 19, o: 0.55 },
    { x: 33, h: 42, o: 1 },    { x: 42, h: 29, o: 0.8 }, { x: 51, h: 23, o: 0.55 },
  ]
  return (
    <svg width={size} height={size} viewBox="0 0 63 64" aria-hidden="true" role="img">
      {bars.map((b, i) => (
        <rect key={i} x={b.x} y={32 - b.h / 2} width="6" height={b.h} rx="3" fill="var(--accent)" opacity={b.o} />
      ))}
    </svg>
  )
}

export function Logo({ size = 30 }: { size?: number }) {
  return (
    <Link to="/preview" className="ork-logo" aria-label="Orkestra home">
      <LogoMark size={size} />
      <span className="ork-logo__word">Orkestra</span>
    </Link>
  )
}

// ─── Buttons ──────────────────────────────────────────────────────────────
type BtnProps = {
  to?: string
  href?: string
  children: React.ReactNode
  variant?: 'primary' | 'ghost'
  size?: 'md' | 'lg'
  block?: boolean
  className?: string
  onClick?: () => void
}
export function Button({ to, href, children, variant = 'primary', size = 'md', block, className = '', onClick }: BtnProps) {
  const cls = `ork-btn ork-btn--${variant}${size === 'lg' ? ' ork-btn--lg' : ''}${block ? ' ork-btn--block' : ''} ${className}`.trim()
  if (to) return <Link to={to} className={cls} onClick={onClick}>{children}</Link>
  if (href) return <a href={href} className={cls} onClick={onClick}>{children}</a>
  return <button type="button" className={cls} onClick={onClick}>{children}</button>
}

// ─── Reveal-on-scroll ─────────────────────────────────────────────────────
// One shared IntersectionObserver-free approach per element: cheap, reliable,
// and respects reduced-motion (CSS handles the no-op). Returns a ref + visible.
export function useReveal<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return }
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { setVisible(true); io.disconnect() } }),
      { threshold: 0.14, rootMargin: '0px 0px -8% 0px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])
  return { ref, visible }
}

export function Reveal({ children, className = '', delay = 0, as = 'div' }: { children: React.ReactNode; className?: string; delay?: number; as?: 'div' | 'section' | 'li' }) {
  const { ref, visible } = useReveal<HTMLDivElement>()
  const Tag = as as any
  return (
    <Tag ref={ref} className={`ork-reveal ${className}`.trim()} data-visible={visible} style={delay ? { transitionDelay: `${delay}ms` } : undefined}>
      {children}
    </Tag>
  )
}

// ─── Sticky-scroll flag (for nav border) ──────────────────────────────────
export function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [threshold])
  return scrolled
}

// ─── Section head (eyebrow + title + lead), centered ──────────────────────
export function SectionHead({ eyebrow, title, lead }: { eyebrow: string; title: React.ReactNode; lead?: React.ReactNode }) {
  return (
    <Reveal className="ork-section-head">
      <span className="ork-eyebrow ork-eyebrow--pill">{eyebrow}</span>
      <h2 className="ork-h2">{title}</h2>
      {lead && <p className="ork-lead">{lead}</p>}
    </Reveal>
  )
}

// ─── Avatar chip (initials on gradient) ───────────────────────────────────
export function Avatar({ initials, grad, size = 26, ring = false }: { initials: string; grad: string; size?: number; ring?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size, height: size, borderRadius: '50%', background: grad,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.4, fontWeight: 700, color: 'var(--accent-ink)', flexShrink: 0,
        boxShadow: ring ? '0 0 0 2px var(--surface-raised)' : undefined,
      }}
    >
      {initials}
    </span>
  )
}
